import { memo, useMemo } from "react";
import groupBy from "lodash-es/groupBy";
import { useBusinessProfile } from "hooks/useFetch";
import useTranslation from "next-translate/useTranslation";
import {
  AppointmentType,
  defaultServiceSorting,
  ServiceFieldsType,
} from "types/business.types";
import { PaginateGenericType } from "types/generic.types";
import Accordion from "components/Accordion/Accordion";
import ServiceCard from "components/Services/Service.card";
import { map, omit, sortBy, toLower, values } from "lodash-es";
import { sortListByAnotherList } from "utils/utils";

type Props = {
  services: PaginateGenericType<ServiceFieldsType>;
  sorting: defaultServiceSorting;
  serviceSettings?: string;
};

function ServicesCategorized({ services, sorting, serviceSettings }: Props) {
  const { t } = useTranslation("common");
  const { data: businessProfile } = useBusinessProfile();
  const widgetSetting = businessProfile?.data.widget;
  const unavailableItems = widgetSetting?.settings.service.unavailableItems;
  const categorized: Array<Array<ServiceFieldsType>> = useMemo(() => {
    const serviceItems = services.items;
    let sortedServices: Array<ServiceFieldsType> = [];
    switch (sorting) {
      case defaultServiceSorting.ALPHABETICALLY:
      case defaultServiceSorting.AVAILABILITY:
        sortedServices = sortBy(serviceItems, [
          (offering: ServiceFieldsType) => offering.name.toLowerCase(),
        ]);
        break;

      case defaultServiceSorting.CUSTOM:
        let customOrder: Array<any> = [];
        try {
          customOrder = serviceSettings ? JSON.parse(serviceSettings) : [];
          customOrder = sortBy(customOrder, "order");
        } catch (e) {
          console.error("Error in sorting offerings settings", e);
        }
        sortedServices = sortListByAnotherList(serviceItems, customOrder);
        break;

      default:
        sortedServices = sortBy(serviceItems, toLower(sorting));
    }

    const uncategorizedGroupLabel = t("sections.services.others");
    const groupedItems = groupBy(
      sortedServices,
      (v) => v.category?.name || uncategorizedGroupLabel
    );

    const uncategorizedGroup = groupedItems[uncategorizedGroupLabel];
    if (uncategorizedGroup) {
      return [
        ...values(omit(groupedItems, uncategorizedGroupLabel)),
        uncategorizedGroup,
      ];
    } else {
      return values(groupedItems);
    }
  }, [serviceSettings, services, sorting, t]);

  return (
    <>
      {map(categorized, (items, i) => {
        return (
          <Accordion
            key={i}
            defaultOpen={i === 0}
            title={items?.[0]?.category?.name || t("sections.services.others")}
            totalItems={items?.length}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {items?.map?.((service: ServiceFieldsType) => {
                return (
                  <ServiceCard
                    id={service.id}
                    key={service.id}
                    hasVirtualBadge={
                      service.appointmentType === AppointmentType.Virtual
                    }
                    title={service.name}
                    description={service.description}
                    imageUrl={service.image}
                    duration={service.duration}
                    price={service.price}
                    unavailableItems={unavailableItems}
                  />
                );
              })}
            </div>
          </Accordion>
        );
      })}
    </>
  );
}

export default memo(ServicesCategorized);
